import { Button } from "@faire/slate/components/button";
import { Checkbox } from "@faire/slate/components/checkbox";
import { FilterToggle } from "@faire/slate/components/filter";
import { Search } from "@faire/slate/components/search";
import React, { useState } from "react";

import { SlateStyleOverride } from "../../../../components/shared";
import type { PillFilterSection } from "../constants";

import { APPLIED_CHIP_OUTLINE } from "./appliedChipOutline";

interface PillFilterSectionContentProps {
  section: PillFilterSection;
  checkedValues: Set<string>;
  onToggleChecked: (value: string) => void;
  /**
   * Changes each time the modal is reopened. Collapses the picker without
   * remounting, so locations already moved up to the pill row stay there.
   */
  resetToken: number;
}

/**
 * Locations as toggle pills rather than a checkbox list. The section starts
 * with the handful of locations worth offering unprompted; everything else
 * arrives through "Add location", which swaps itself for a search over the
 * rest.
 *
 * Ticking a row moves it out of the list and into the pills, ahead of the
 * quick options, so the list only ever offers what isn't already chosen and
 * the row of pills is the full picture of what's applied. A pill stays put
 * once promoted — unapplying it only clears its applied state, because
 * sending it back to the list would reflow both under the cursor. Reopening
 * the modal collapses the picker; only a page load clears the promotions.
 */
export const PillFilterSectionContent: React.FC<
  PillFilterSectionContentProps
> = ({ section, checkedValues, onToggleChecked, resetToken }) => {
  const [isPicking, setIsPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [isShowingMore, setIsShowingMore] = useState(false);
  /*
   * Locations moved up to the pill row, in the order they were picked. Held
   * separately from `checkedValues` so unapplying one leaves it in place
   * rather than dropping it back into the list and reflowing everything
   * under the cursor. Seeded from what is already applied, for a section
   * whose values were set from the chip row or a previous visit.
   */
  const [promotedValues, setPromotedValues] = useState<string[]>(() =>
    [...checkedValues].filter((value) =>
      section.searchableOptions.some((option) => option.value === value)
    )
  );

  const [lastResetToken, setLastResetToken] = useState(resetToken);
  if (resetToken !== lastResetToken) {
    setLastResetToken(resetToken);
    setIsPicking(false);
    setQuery("");
    setIsShowingMore(false);
  }

  const promotedOptions = promotedValues.flatMap((value) => {
    const option = section.searchableOptions.find(
      (candidate) => candidate.value === value
    );
    return option === undefined ? [] : [option];
  });
  const pills = [...promotedOptions, ...section.options];

  const trimmedQuery = query.trim().toLowerCase();
  const matches = section.searchableOptions.filter(
    (option) =>
      !promotedValues.includes(option.value) &&
      option.label.toLowerCase().includes(trimmedQuery)
  );

  // A search shows everything it matches; unsearched, the list is the top few
  // until "Show more".
  const isListCapped = trimmedQuery === "" && !isShowingMore;
  const visibleCandidates = isListCapped
    ? matches.slice(0, VISIBLE_CANDIDATE_COUNT)
    : matches;
  const hasMoreToReveal =
    trimmedQuery === "" && matches.length > VISIBLE_CANDIDATE_COUNT;

  return (
    <div className="flex flex-col gap-(--slate-dimensions-3)">
      <SlateStyleOverride className={APPLIED_CHIP_OUTLINE}>
        <div className="flex flex-wrap gap-(--slate-dimensions-1)">
          {pills.map((option) => (
            <FilterToggle
              key={option.value}
              applied={checkedValues.has(option.value)}
              label={option.label}
              onClick={() => onToggleChecked(option.value)}
            />
          ))}
        </div>
      </SlateStyleOverride>

      {isPicking ? (
        <div className="flex flex-col gap-(--slate-dimensions-3)">
          {/* Slate's Search is a full pill; the design uses the squarer
              search-page shape, and there is no radius token between 4px and
              999px to reach it with. */}
          <SlateStyleOverride className={SQUARER_SEARCH}>
            <Search
              aria-label={section.addPlaceholder}
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
              onClear={() => setQuery("")}
              placeholder={section.addPlaceholder}
              value={query}
            />
          </SlateStyleOverride>

          {visibleCandidates.length === 0 ? (
            <p className="type-fs-paragraph text-fs-text-subdued">
              No matching locations
            </p>
          ) : (
            <div className="flex flex-col gap-(--slate-dimensions-2)">
              {visibleCandidates.map((option) => (
                <Checkbox
                  key={option.value}
                  checked={checkedValues.has(option.value)}
                  id={option.value}
                  label={option.label}
                  onChange={() => {
                    onToggleChecked(option.value);
                    setPromotedValues((previous) => [
                      ...previous,
                      option.value,
                    ]);
                  }}
                />
              ))}
            </div>
          )}

          {hasMoreToReveal ? (
            <div>
              <Button
                onClick={() => setIsShowingMore(true)}
                size="small"
                variant="plain"
              >
                Show more
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div>
          <Button
            onClick={() => setIsPicking(true)}
            size="small"
            variant="plain"
          >
            {section.addLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

/** Candidates shown before "Show more" opens the rest. */
const VISIBLE_CANDIDATE_COUNT = 3;

/** Measured off the design: 16px on a 40px field, against Slate's 999px. */
const SQUARER_SEARCH = "[&_.fs-search]:rounded-(--slate-dimensions-4)";
